import { useEffect, useState, useCallback, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import { LinkIcon } from '@heroicons/react/20/solid';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Heading } from '../components_catalyst/heading';
import { Text } from '../components_catalyst/text';

function AnchorHeading({
  level,
  id,
  children,
}: {
  level: 2 | 3;
  id?: string;
  children: ReactNode;
}) {
  const handleCopyLink = useCallback(() => {
    if (!id) return;
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url);
    window.location.hash = id;
  }, [id]);

  const Tag = level === 2 ? 'h2' : 'h3';
  const className =
    level === 2
      ? 'group relative text-xl font-semibold mt-8 mb-3 pb-2 border-b border-zinc-200 dark:border-zinc-700 text-zinc-950 dark:text-zinc-50'
      : 'group relative text-base font-semibold mt-4 mb-2 text-zinc-700 dark:text-zinc-300';

  return (
    <Tag id={id} className={className}>
      {id && (
        <button
          onClick={handleCopyLink}
          className="absolute -left-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Copy link to section"
        >
          <LinkIcon className="h-4 w-4 text-zinc-950 dark:text-zinc-50" />
        </button>
      )}
      {children}
    </Tag>
  );
}

export function ChangelogPage() {
  useDocumentTitle('Changelog');

  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch('/CHANGELOG.md', { cache: 'no-cache' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch changelog');
        return res.text();
      })
      .then((text) => setContent(text))
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Heading level={1} className="mb-6">
          Changelog
        </Heading>
        <Text>Unable to load changelog.</Text>
        <button
          onClick={() => {
            setError(false);
            setContent(null);
            fetch('/CHANGELOG.md', { cache: 'no-cache' })
              .then((res) => {
                if (!res.ok) throw new Error('Failed to fetch changelog');
                return res.text();
              })
              .then((text) => setContent(text))
              .catch(() => setError(true));
          }}
          className="mt-4 text-blue-600 hover:text-blue-800 hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (content === null) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Heading level={1} className="mb-6">
          Changelog
        </Heading>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-3/4" />
          <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-1/2" />
          <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-5/6" />
        </div>
      </div>
    );
  }

  // Remove the top-level "# Changelog" heading since we render our own
  const body = content.replace(/^# Changelog\n+/, '');

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Heading level={1} className="mb-6">
        Changelog
      </Heading>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
        components={{
          h2: ({ id, children }: { id?: string; children?: ReactNode }) => (
            <AnchorHeading level={2} id={id}>
              {children}
            </AnchorHeading>
          ),
          h3: ({ id, children }: { id?: string; children?: ReactNode }) => (
            <AnchorHeading level={3} id={id}>
              {children}
            </AnchorHeading>
          ),
          ul: ({ children }: { children?: ReactNode }) => (
            <ul className="list-disc pl-6 space-y-1 text-sm text-zinc-600 dark:text-zinc-400">
              {children}
            </ul>
          ),
          li: ({ children }: { children?: ReactNode }) => <li className="leading-relaxed">{children}</li>,
          a: ({ href, children }: { href?: string; children?: ReactNode }) => (
            <a
              href={href}
              className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
            >
              {children}
            </a>
          ),
          p: ({ children }: { children?: ReactNode }) => (
            <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
              {children}
            </p>
          ),
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}
